import { useCallback, useEffect, useRef, useState } from "react";
import lottoApi from "./api";
import { useAuth } from "../contexts/JWTContext";

/*
 * คืนยอดเสีย — ตรรกะฝั่งลูกค้า (ใช้ร่วมกันทั้ง landing และ landing-ver2; ไฟล์นี้เหมือนกันทั้งสองแอป)
 * หน้าตาแยกกันที่ pages/Cashback.js (landing = ร่มรับเงินคืนสดใส · landing-ver2 = แดชบอร์ดนีออน)
 *   • เซิร์ฟเวอร์คำนวณ/ตัดสินทุกอย่าง (ยอดเสียของหวย สิทธิ์ อายุ เทิร์น เปิด-ปิดระบบ) — ฝั่งนี้แค่แสดงผลและส่งคำสั่งกดรับ
 */

const nf = (v) => Number(v || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });
export const fmtBaht = (v) => `฿${nf(v)}`;

const THAI_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const THAI_DAYS = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];

/** "2026-10-29" → { day: "29", month: "ต.ค.", dow: "พฤ.", label: "29 ต.ค." } (อ่านเป็นวันที่ล้วน ๆ ไม่ผ่าน timezone) */
export function dayParts(ymd) {
  const m = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { day: "", month: "", dow: "", label: "" };
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const month = THAI_MONTHS[Number(m[2]) - 1];
  return { day: String(Number(m[3])), month, dow: THAI_DAYS[d.getUTCDay()], label: `${Number(m[3])} ${month}` };
}

/** เหลือเวลากดรับกี่วัน → ข้อความ + ระดับความเร่งด่วน */
export function expiryText(p) {
  const left = Number(p && p.days_left);
  if (!(left >= 0)) return { text: "", urgent: false };
  if (left === 0) return { text: "หมดสิทธิ์วันนี้", urgent: true };
  return { text: `เหลือ ${left} วัน`, urgent: left <= 1 };
}

export const STATUS_LABEL = { UNRECEIVE: "รอกดรับ", RECEIVED: "รับแล้ว", TIMEOUT: "หมดสิทธิ์", TODAY: "วันนี้", NONE: "ไม่มียอดเสีย" };

/** ข้อความประมาณการของวันนี้ */
export function todayText(today) {
  if (!today) return "";
  if (today.net_loss <= 0) return "วันนี้ยังไม่มียอดเสียที่ออกผล";
  return today.estimate > 0
    ? `วันนี้เสียสุทธิ ${fmtBaht(today.net_loss)} → คาดว่าจะได้คืน ${fmtBaht(today.estimate)}`
    : `วันนี้เสียสุทธิ ${fmtBaht(today.net_loss)} (ยังไม่ถึงเกณฑ์ที่จะได้คืน)`;
}

export const turnPct = (a) => (a && a.turnover_required > 0 ? Math.max(0, Math.min(100, (a.turnover_done / a.turnover_required) * 100)) : 0);

export { whenText } from "./creditCodeLogic";

const EMPTY = { enabled: true, setting: null, rules: [], today: null, pending: [], history: [], active: [], week: [], totals: { received: 0, pending: 0, count: 0 } };

export function useCashback(onDone) {
  const { user, isAuthenticated } = useAuth();
  const uid = isAuthenticated ? user?.id : null;
  const [view, setView] = useState(null); // null = ยังโหลดไม่เสร็จ
  const [claimingId, setClaimingId] = useState(null); // id | "ALL"
  const [result, setResult] = useState(null); // { ok:true, ...claim } | { ok:false, message }
  const alive = useRef(true);
  const busy = useRef(false);
  const cb = useRef(onDone);
  cb.current = onDone;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!uid) return Promise.resolve();
    return Promise.resolve()
      .then(() => lottoApi.myCashback())
      .then((v) => alive.current && setView({ ...EMPTY, ...(v || {}) }))
      .catch(() => alive.current && setView((prev) => prev || { ...EMPTY, failed: true }));
  }, [uid]);

  useEffect(() => {
    setView(null);
    setResult(null);
    load();
    const onVis = () => !document.hidden && load();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  /** id ไม่ระบุ = รับทุกรายการที่รับได้ */
  const claim = useCallback(
    async (id = null) => {
      if (busy.current) return;
      busy.current = true;
      setClaimingId(id === null ? "ALL" : id);
      try {
        const res = await lottoApi.claimCashback(id);
        if (alive.current) setResult({ ok: true, ...(res?.data || {}) });
        if (cb.current) cb.current(res?.data);
      } catch (err) {
        const d = err?.response?.data;
        if (alive.current) setResult({ ok: false, code: d?.code || "ERROR", message: d?.message || "รับคืนยอดเสียไม่สำเร็จ ลองใหม่อีกครั้ง" });
      } finally {
        busy.current = false;
        if (alive.current) setClaimingId(null);
        load();
      }
    },
    [load],
  );

  return {
    isAuthenticated,
    loading: !!uid && view === null,
    failed: !!(view && view.failed),
    enabled: view ? view.enabled !== false : true,
    view: view || EMPTY,
    claimingId,
    claim,
    result,
    closeResult: () => setResult(null),
    reload: load,
  };
}
